import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import ConfirmModal from '../components/ConfirmModal';

const money = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value || 0);

export default function OrderDetail() {
  const { id } = useParams();
  const { token } = useAuth();
  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState(null);

  const loadOrder = async () => {
    const data = await api.get(`/orders/${id}`, token);
    setOrder(data);
    setStatus(data.status);
  };

  useEffect(() => {
    loadOrder();
  }, [id, token]);

  const saveStatus = async () => {
    setSaving(true);
    setMessage('');

    try {
      await api.patch(`/orders/${id}/status`, { status, admin_notes: notes }, token);
      setNotes('');
      setMessage('Order updated successfully');
      await loadOrder();
    } catch (error) {
      setMessage(error.message || 'Failed to update order');
    } finally {
      setSaving(false);
      setConfirmModal(null);
    }
  };

  const requestArchive = () => {
    setConfirmModal({
      title: 'Archive this order?',
      message: 'The order stays in the database but will be hidden from the default orders list.',
      confirmLabel: 'Archive order',
      onConfirm: async () => {
        setSaving(true);
        setMessage('');

        try {
          await api.patch(
            `/orders/${id}/status`,
            { status: 'archived', admin_notes: notes },
            token
          );
          setNotes('');
          setMessage('Order archived successfully');
          await loadOrder();
        } catch (error) {
          setMessage(error.message || 'Failed to archive order');
        } finally {
          setSaving(false);
          setConfirmModal(null);
        }
      }
    });
  };

  if (!order) return <div className="card p-6">Loading order...</div>;

  return (
    <div className="space-y-6">
      <div className="card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-sm text-slate-500">Order</p>
            <h1 className="text-3xl font-bold">{order.order_number}</h1>
            <p className="mt-2 text-slate-600">{order.shipping_name}</p>
            <p className="text-slate-600">{order.email}</p>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4">
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold">{money(order.total)}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Size</th>
                  <th>Qty</th>
                  <th>Price</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item) => (
                  <tr key={item.id}>
                    <td>{item.product_name}</td>
                    <td>{item.size}</td>
                    <td>{item.quantity}</td>
                    <td>{money(item.price_at_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card p-5 space-y-2">
            <h3 className="text-lg font-semibold">Shipping Info</h3>

            <p className="font-medium">{order.shipping_name || 'No name saved'}</p>

            <p>{order.shipping_address_1 || order.shipping_address_line1 || 'No address saved'}</p>

            {(order.shipping_address_2 || order.shipping_address_line2) && (
              <p>{order.shipping_address_2 || order.shipping_address_line2}</p>
            )}

            <p>
              {order.shipping_city || 'No city'}, {order.shipping_state || 'No state'}{' '}
              {order.shipping_zip || 'No ZIP'}
            </p>

            <p>{order.shipping_phone || 'No phone saved'}</p>
          </div>
        </div>

        <div className="card p-5 space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium">Status</label>
            <select
              className="w-full rounded-xl border border-slate-200 px-3 py-2 min-h-[44px]"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="new">New</option>
              <option value="processing">Processing</option>
              <option value="packed">Packed</option>
              <option value="shipped">Shipped</option>
              <option value="completed">Completed</option>
              <option value="delivered">Delivered</option>
              <option value="archived">Archived</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {message ? (
            <div className={`rounded-xl px-3 py-2 text-sm ${
              message.toLowerCase().includes('fail')
                ? 'bg-red-50 text-red-700'
                : 'bg-emerald-50 text-emerald-700'
            }`}>
              {message}
            </div>
          ) : null}

          <div>
            <label className="mb-2 block text-sm font-medium">Admin Notes</label>
            <textarea
              className="min-h-[120px] w-full rounded-xl border border-slate-200 px-3 py-2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <button
            onClick={saveStatus}
            disabled={saving}
            className="w-full rounded-xl bg-orange-600 px-4 py-3 font-semibold text-white min-h-[44px] disabled:opacity-60"
          >
            {saving ? 'Saving...' : 'Update Order'}
          </button>

          {order.status !== 'archived' ? (
            <button
              type="button"
              onClick={requestArchive}
              disabled={saving}
              className="w-full rounded-xl bg-slate-700 px-4 py-3 font-semibold text-white min-h-[44px] disabled:opacity-60"
            >
              Archive Order
            </button>
          ) : null}
        </div>
      </div>

      <ConfirmModal
        open={Boolean(confirmModal)}
        title={confirmModal?.title || ''}
        message={confirmModal?.message || ''}
        confirmLabel={confirmModal?.confirmLabel || 'Confirm'}
        loading={saving}
        onCancel={() => setConfirmModal(null)}
        onConfirm={() => confirmModal?.onConfirm?.()}
      />
    </div>
  );
}